/* * Version: 3.4.0
 * Letzte Änderung: 16.09.2026, 14:30
 * Änderungshistorie: Unterstützung für den Multi-Mode-Filter über URL-Parameter (?gameMode=) hinzugefügt, um exakte Ranglisten im Stream anzuzeigen.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useMemo } from 'react';
// NEU: useSearchParams importiert, um den ?gameMode Parameter aus der URL zu lesen
import { useParams, useSearchParams } from 'react-router-dom';
import apiClient from '../api';
import { CLASSIFICATIONS } from '../constants/classificationConstants';
import { GAME_MODES } from '../constants/gameModeConstants';

const StreamerViewScreen = () => {
  const { tournamentId, classificationKey } = useParams();
  const [searchParams] = useSearchParams();
  
  // Extrahiert den Spielmodus aus der URL, z.B. "CLASSIC_120" oder "SPRINT"
  const gameModeFilter = searchParams.get('gameMode');
  
  const [scores, setScores] = useState([]);
  const [classificationLabel, setClassificationLabel] = useState('');
  const [loading, setLoading] = useState(true);

  const isTeamView = classificationKey.startsWith('team_');

  const getClassificationLabel = (key) => CLASSIFICATIONS.find(c => c.key === key)?.label || key;
  const getGameModeLabel = (key) => GAME_MODES.find(g => g.key === key)?.label || key;
  
  const fetchData = async () => {
    try {
      const { data } = await apiClient.get(`/api/tournaments/${tournamentId}/scores`);
      setScores(data);
      setClassificationLabel(getClassificationLabel(classificationKey));
    } catch (err) {
      console.error("Fehler beim Laden der Stream-Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Lädt die Daten alle 30 Sekunden im Hintergrund neu (ideal für Live-Streams)
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [tournamentId, classificationKey]);

  // Hilfsfunktionen für Farbcodierung
  const getColorForValue = (value, type) => {
    let thresholds;
    if (type === 'team-total') {
      thresholds = { blue: 2400, green: 2160, red: 2000 };
    } else { // Gilt für Einzelschnitt und Mannschaftsschnitt
      thresholds = { blue: 600, green: 540, red: 500 };
    }

    if (value >= thresholds.blue) return '#007bff';
    if (value >= thresholds.green) return '#28a745';
    if (value >= thresholds.red) return '#dc3545';
    return '#000000'; 
  };
  
  const getAffiliationName = (entry) => {
    if (!entry) return 'Vereinslos';
    if (entry.team?.name) return entry.team.name;
    if (entry.player?.club?.name) return entry.player.club.name;
    if (entry.player?.guestClubName) return entry.player.guestClubName;
    return 'Vereinslos';
  };

  // Berechnet die Einzelspieler-Rangliste
  const sortedSingleScores = useMemo(() => {
    if (isTeamView) return [];
    return scores
      .filter(score => score.entry && score.entry.player && score.entry.classification === classificationKey)
      // NEU: Filtern nach Spielmodus, falls ein Parameter in der URL übergeben wurde
      .filter(score => gameModeFilter ? (score.gameMode || 'CLASSIC_120') === gameModeFilter : true)
      .sort((a, b) => {
        if (b.totalGesamt !== a.totalGesamt) return b.totalGesamt - a.totalGesamt;
        if (b.totalAbr !== a.totalAbr) return b.totalAbr - a.totalAbr;
        return a.totalFw - b.totalFw;
      });
  }, [scores, classificationKey, isTeamView, gameModeFilter]);

  // Berechnet die Mannschafts-Rangliste
  const sortedTeamResults = useMemo(() => {
    if (!isTeamView) return [];
    const validScores = scores.filter(score => score.entry && score.entry.player);
    
    // NEU: Zuerst nach Spielmodus filtern, damit die Mannschaften nicht Scores aus anderen Disziplinen einsammeln
    const filteredScores = gameModeFilter 
      ? validScores.filter(s => (s.gameMode || 'CLASSIC_120') === gameModeFilter) 
      : validScores;

    const teams = filteredScores.reduce((acc, score) => {
      if (score.entry?.team?.classification !== classificationKey) return acc;
      
      const teamId = score.entry.team._id;
      if (!acc[teamId]) {
        acc[teamId] = {
          _id: teamId,
          name: score.entry.team.name,
          scores: [],
        };
      }
      acc[teamId].scores.push(score);
      return acc;
    }, {});
    
    return Object.values(teams).map(team => {
      const topScores = team.scores.sort((a, b) => b.totalGesamt - a.totalGesamt).slice(0, 4);
      const totalVolle = topScores.reduce((sum, s) => sum + s.totalVolle, 0);
      const totalAbr = topScores.reduce((sum, s) => sum + s.totalAbr, 0);
      const totalFw = topScores.reduce((sum, s) => sum + s.totalFw, 0);
      const totalGesamt = totalVolle + totalAbr;
      const average = topScores.length > 0 ? (totalGesamt / topScores.length) : 0;
      return { ...team, totalVolle, totalAbr, totalFw, totalGesamt, average: average.toFixed(2) };
    }).sort((a, b) => {
        if (b.totalGesamt !== a.totalGesamt) return b.totalGesamt - a.totalGesamt;
        if (b.totalAbr !== a.totalAbr) return b.totalAbr - a.totalAbr; 
        return a.totalFw - b.totalFw;
    });
  }, [scores, classificationKey, isTeamView, gameModeFilter]);

  if (loading) return <div className="streamer-view"><p>Lade Ergebnisse...</p></div>;

  return (
    <div className="streamer-view">
      {/* Anzeige des Spielmodus im Titel, damit Zuschauer wissen, was sie sehen */}
      <h3>{classificationLabel} {gameModeFilter ? `(${getGameModeLabel(gameModeFilter)})` : ''}</h3>
      
      {isTeamView ? (
        <table>
          <thead>
            <tr>
              <th>Rang</th>
              <th>Mannschaft</th>
              <th>Volle</th>
              <th>Abr.</th>
              <th>Gesamt</th>
              <th>Schnitt</th>
              <th>FW</th>
            </tr>
          </thead>
          <tbody>
            {sortedTeamResults.map((team, index) => (
              <tr key={team._id} className={index < 3 ? 'top-three' : ''}>
                <td>{index + 1}.</td>
                <td>{team.name}</td>
                <td style={{ textAlign: 'center' }}>{team.totalVolle}</td>
                <td style={{ textAlign: 'center' }}>{team.totalAbr}</td>
                <td style={{ fontWeight: 'bold', color: getColorForValue(team.totalGesamt, 'team-total'), textAlign: 'center' }}>
                  {team.totalGesamt}
                </td>
                <td style={{ fontWeight: 'bold', color: getColorForValue(team.average, 'team-average'), textAlign: 'center' }}>
                  {team.average}
                </td>
                <td style={{ textAlign: 'center' }}>{team.totalFw}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Rang</th>
              <th>Name</th>
              <th>Verein</th>
              <th>Volle</th>
              <th>Abr.</th>
              <th>FW</th>
              <th>Gesamt</th>
            </tr>
          </thead>
          <tbody>
            {sortedSingleScores.map((score, index) => (
              <tr key={score._id} className={index < 3 ? 'top-three' : ''}>
                <td>{index + 1}.</td>
                <td>{score.entry.player.lastName}, {score.entry.player.firstName}</td>
                <td>{getAffiliationName(score.entry)}</td>
                <td style={{ textAlign: 'center' }}>{score.totalVolle}</td>
                <td style={{ textAlign: 'center' }}>{score.totalAbr}</td>
                <td style={{ textAlign: 'center' }}>{score.totalFw}</td>
                <td style={{ color: getColorForValue(score.totalGesamt, 'single'), fontWeight: 'bold' }}>
                  {score.totalGesamt}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default StreamerViewScreen;